import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, money } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | pending | error
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const max = 8;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setDetails(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("error");
          return;
        }
        attempts += 1;
        if (attempts >= max) { setStatus("pending"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        attempts += 1;
        if (attempts >= max) { setStatus("error"); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-4 py-24 text-center">
      {status === "checking" && (
        <>
          <Loader2 className="w-12 h-12 text-orange-500 mx-auto animate-spin" />
          <h1 className="mt-6 font-display text-4xl font-black uppercase tracking-tighter">Confirming payment…</h1>
          <p className="mt-2 text-zinc-500 text-sm">Please don&apos;t close this window.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
          <h1 data-testid="checkout-success" className="mt-6 font-display text-5xl font-black uppercase tracking-tighter text-white">Payment Received.</h1>
          <p className="mt-4 text-zinc-400 max-w-md mx-auto">
            You paid <b className="text-orange-500 font-display text-lg">{money(details?.amount_total ? details.amount_total / 100 : 0)}</b>. Show your receipt at the facility within 48 hours to collect your unit.
          </p>
          <Button asChild className="mt-8 rounded-none bg-orange-600 hover:bg-orange-500 border border-orange-500 h-12 px-8 text-sm uppercase tracking-widest font-semibold">
            <Link to="/dashboard">Go to Dashboard</Link>
          </Button>
        </>
      )}
      {status === "pending" && (
        <>
          <Loader2 className="w-12 h-12 text-orange-500 mx-auto" />
          <h1 className="mt-6 font-display text-4xl font-black uppercase tracking-tighter">Still Processing…</h1>
          <p className="mt-4 text-zinc-400 max-w-md mx-auto">Payment is taking longer than expected. Check your dashboard shortly.</p>
          <Button asChild className="mt-8 rounded-none bg-orange-600 hover:bg-orange-500 border border-orange-500 h-12 px-8">
            <Link to="/dashboard">Go to Dashboard</Link>
          </Button>
        </>
      )}
      {status === "error" && (
        <>
          <XCircle className="w-16 h-16 text-red-500 mx-auto" />
          <h1 className="mt-6 font-display text-4xl font-black uppercase tracking-tighter">Payment Failed</h1>
          <p className="mt-4 text-zinc-400">Something went wrong. Please try again from your dashboard.</p>
          <Button asChild className="mt-8 rounded-none bg-orange-600 hover:bg-orange-500 border border-orange-500 h-12 px-8">
            <Link to="/dashboard">Back to Dashboard</Link>
          </Button>
        </>
      )}
    </div>
  );
}
